import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Alert,
  Chip,
  Grid
} from '@mui/material';
import { useQuizConfiguration, useGenerateQuiz } from '../../../hooks/useQuizApi';

const QuizConfig = ({ onComplete, onBack, initialConfig = {} }) => {
  const [config, setConfig] = useState({
    difficulty_id: initialConfig.difficulty_id || '',
    topic_id: initialConfig.topic_id || '',
    duration_minutes: initialConfig.duration_minutes || 5,
    questions_per_song: initialConfig.questions_per_song || 10
  });

  const [errors, setErrors] = useState({});

  const { 
    data: configData, 
    isLoading: configLoading, 
    error: configError 
  } = useQuizConfiguration();

  const generateQuiz = useGenerateQuiz({
    onSuccess: (data) => {
      onComplete(config, data);
    },
    onError: (error) => {
      setErrors({ submit: error.message });
    }
  });

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    // Clear error when user makes a change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateConfig = () => {
    const newErrors = {};

    if (!config.difficulty_id) {
      newErrors.difficulty_id = 'Please select a difficulty level';
    }

    if (!config.duration_minutes) {
      newErrors.duration_minutes = 'Please select quiz duration';
    }

    if (!config.questions_per_song) {
      newErrors.questions_per_song = 'Please select questions per song';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateConfig()) {
      return;
    }

    generateQuiz.mutate(config);
  };

  const getDerivedStats = () => {
    if (!config.duration_minutes || !config.questions_per_song) {
      return null;
    }

    return {
      totalSongs: config.duration_minutes,
      totalQuestions: config.duration_minutes * config.questions_per_song,
      estimatedTime: `${config.duration_minutes} minutes`
    };
  };

  const derivedStats = getDerivedStats();

  if (configLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (configError) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Alert severity="error">
          Failed to load quiz configuration. Please try again.
        </Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#1976d2',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Reload Page
          </button>
        </Box>
      </Box>
    );
  }

  if (!configData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Quiz Configuration
        </Typography>
        
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Configure your quiz settings to get started
        </Typography>

        <Grid container spacing={3}>
          {/* Difficulty Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.difficulty_id}>
              <InputLabel>Difficulty Level</InputLabel>
              <Select
                value={config.difficulty_id}
                label="Difficulty Level"
                onChange={(e) => handleConfigChange('difficulty_id', e.target.value)}
              >
                <MenuItem value="all">
                  <Box>
                    <Typography variant="body1">All Difficulties</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mix of all difficulty levels
                    </Typography>
                  </Box>
                </MenuItem>
                {configData?.difficulties?.map((difficulty) => (
                  <MenuItem key={difficulty.difficulty_id} value={difficulty.difficulty_id}>
                    <Box>
                      <Typography variant="body1">{difficulty.level_name}</Typography>
                      {difficulty.description && (
                        <Typography variant="caption" color="text.secondary">
                          {difficulty.description}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.difficulty_id && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {errors.difficulty_id}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Topic Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Topic (Optional)</InputLabel>
              <Select
                value={config.topic_id}
                label="Topic (Optional)"
                onChange={(e) => handleConfigChange('topic_id', e.target.value)}
              >
                <MenuItem value="">All Topics</MenuItem>
                {configData?.topics?.map((topic) => (
                  <MenuItem key={topic.topic_type_id} value={topic.topic_type_id}>
                    {topic.display_name || topic.topic_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Duration Selection */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Quiz Duration
            </Typography>
            <RadioGroup
              value={config.duration_minutes}
              onChange={(e) => handleConfigChange('duration_minutes', parseInt(e.target.value))}
            >
              {configData?.durations?.map((duration) => (
                <FormControlLabel
                  key={duration}
                  value={duration}
                  control={<Radio />}
                  label={`${duration} minute${duration > 1 ? 's' : ''}`}
                />
              ))}
            </RadioGroup>
            {errors.duration_minutes && (
              <Typography variant="caption" color="error">
                {errors.duration_minutes}
              </Typography>
            )}
          </Grid>

          {/* Questions per Song */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Questions per Song
            </Typography>
            <RadioGroup
              value={config.questions_per_song}
              onChange={(e) => handleConfigChange('questions_per_song', parseInt(e.target.value))}
            >
              {configData?.questionsPerSong?.map((count) => (
                <FormControlLabel
                  key={count}
                  value={count}
                  control={<Radio />}
                  label={`${count} questions`}
                />
              ))}
            </RadioGroup>
            {errors.questions_per_song && (
              <Typography variant="caption" color="error">
                {errors.questions_per_song}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Derived Statistics */}
        {derivedStats && (
          <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Quiz Overview
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip 
                label={`${derivedStats.totalSongs} songs`} 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label={`${derivedStats.totalQuestions} questions`} 
                color="secondary" 
                variant="outlined" 
              />
              <Chip 
                label={derivedStats.estimatedTime} 
                color="info" 
                variant="outlined" 
              />
            </Box>
          </Box>
        )}

        {/* Error Display */}
        {errors.submit && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.submit}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                padding: '16px 32px',
                border: '2px solid #1976d2',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                color: '#1976d2',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '120px'
              }}
            >
              Back
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={generateQuiz.isPending}
            style={{
              padding: '16px 32px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: generateQuiz.isPending ? '#666' : '#1976d2',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: '500',
              cursor: generateQuiz.isPending ? 'not-allowed' : 'pointer',
              opacity: generateQuiz.isPending ? 0.6 : 1,
              transition: 'all 0.2s ease',
              minWidth: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {generateQuiz.isPending && <CircularProgress size={20} style={{ color: '#ffffff' }} />}
            {generateQuiz.isPending ? 'Generating Quiz...' : 'Continue to Rules'}
          </button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuizConfig;
